import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GlobalEventListenerService } from '../../services/global-event-listener.service';
import { filter, tap } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

interface GradientOption {
  value: any;
  gradientValues: string[];
}

@Component({
    selector: 'topo-color-gradient-select',
    templateUrl: './color-gradient-select.component.html',
    styleUrls: ['./color-gradient-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: ColorGradientSelectComponent
        }
    ],
    standalone: false
})
export class ColorGradientSelectComponent implements ControlValueAccessor {
  @Input({ required: true }) gradientOptions: GradientOption[];
  @Input() optionValueKey: string = 'value';
  protected isActive = false;
  protected selectedOption: GradientOption;
  private changeDetector = inject(ChangeDetectorRef);

  @ViewChild('colorGradientSelect') selectEl: ElementRef;
  
  private eventListenerService = inject(GlobalEventListenerService);
  private clickOutsideEvent$ = toSignal(
    this.eventListenerService.clickEvents$.pipe(
      filter(() => this.isActive),
      filter(target => !this.selectEl.nativeElement.contains(target)),
      tap(() => this.toggleIsActive())
    )
  );

  // Value Accessor Implementation
  value: string;
  onChange = value => {};
  onTouched = () => {};
  touched = false;
  disabled = false;

  writeValue(value: any): void {
    this.value = value;
    this.selectedOption = this.gradientOptions.find(o => o[this.optionValueKey].toString() === this.value.toString());
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  getGradientCSS(option: GradientOption): string {
    if (!Boolean(option)) {
      return;
    }

    return 'linear-gradient(to right,' + option.gradientValues.toString() + ')';
  }

  toggleIsActive(): void {
    this.isActive = !this.isActive;
    this.changeDetector.markForCheck();
  }

  selectOption(option: GradientOption): void {
    const value = option[this.optionValueKey];
    this.writeValue(value);
    this.onChange(value);
    this.toggleIsActive();
  }
}
